import { computed, ref, type ComputedRef, type Ref } from 'vue'

import type { Anomaly } from '../models/anomaly.types'
import {
  filterAnomalies,
  uniqueDimensions,
  uniqueMetrics,
  type AnomalyDimension,
} from '../service/filterAnomalies'

import { useAnomalyCatalogContext } from './useAnomalyCatalogContext'

import { useI18n } from '@/shared/i18n'

export interface AnomalyFilterOption {
  value: string | null
  label: string
}

export interface AnomalyDimensionOption extends AnomalyFilterOption {
  dimension: AnomalyDimension | null
}

export interface UseAnomalyFiltersOptions {
  metricLabel?: (field: string) => string
  titleOf?: (anomaly: Anomaly) => string
  translate?: (key: string) => string
}

export interface UseAnomalyFilters {
  searchQuery: Ref<string>
  selectedMetric: Ref<string | null>
  selectedDimension: Ref<string | null>
  metricOptions: ComputedRef<AnomalyFilterOption[]>
  dimensionOptions: ComputedRef<AnomalyDimensionOption[]>
  filtered: ComputedRef<Anomaly[]>
  reset: () => void
}

export function useAnomalyFilters(
  anomalies: Ref<Anomaly[]>,
  options: UseAnomalyFiltersOptions = {},
): UseAnomalyFilters {
  const catalog = options.metricLabel ? null : useAnomalyCatalogContext()
  const i18n = options.translate ? null : useI18n()
  const metricLabel = options.metricLabel ?? catalog?.metricLabel ?? ((field: string) => field)
  const translate = options.translate ?? ((key: string) => i18n?.t(key) ?? key)
  const searchQuery = ref('')
  const selectedMetric = ref<string | null>(null)
  const selectedDimension = ref<string | null>(null)

  const metricOptions = computed<AnomalyFilterOption[]>(() => [
    { value: null, label: translate('anomalies.filters.allMetrics') },
    ...uniqueMetrics(anomalies.value, metricLabel).map((value) => ({
      value,
      label: metricLabel(value),
    })),
  ])
  const dimensionOptions = computed<AnomalyDimensionOption[]>(() => [
    { value: null, label: translate('anomalies.filters.allDimensions'), dimension: null },
    ...uniqueDimensions(anomalies.value).map((dimension) => ({
      value: dimension.value,
      label: `${dimension.segmentName}: ${dimension.subSegmentName}`,
      dimension,
    })),
  ])
  const filtered = computed(() =>
    filterAnomalies(
      anomalies.value,
      {
        metricField: selectedMetric.value,
        dimensionKey: selectedDimension.value,
        query: searchQuery.value,
      },
      { metricLabel, ...(options.titleOf ? { titleOf: options.titleOf } : {}) },
    ),
  )

  function reset(): void {
    searchQuery.value = ''
    selectedMetric.value = null
    selectedDimension.value = null
  }

  return {
    searchQuery,
    selectedMetric,
    selectedDimension,
    metricOptions,
    dimensionOptions,
    filtered,
    reset,
  }
}
